// src/pages/Banners/BannersPage.tsx
import React, {useState, useMemo} from "react";
import {Eye, Edit, Trash2, Plus, Image as ImageIcon} from "lucide-react";
import {useToast} from "../../hooks/common/useToast";
import {useModal} from "../../hooks/common/useModal";
import {useTable} from "../../hooks/common/useTable";
import {useBanners} from "../../hooks/useBanners";
import type {BannerResponseDto} from "../../types/banner";

import {Badge, Button} from "../../components/ui";
import {PageHeader} from "../../components/common";
import {
  createCommonActions,
  DataTable,
} from "../../components/common/DataTable";
import type {
  DataTableAction,
  DataTableColumn,
} from "../../components/common/DataTable/DataTableTypes";

import BannerFormModal from "./components/BannerFormModal";
import BannerPreviewModal from "./components/BannerPreviewModal";

const BannersPage: React.FC = () => {
  const {success} = useToast();

  // Modals
  const createModal = useModal("create-banner");
  const editModal = useModal("edit-banner");
  const previewModal = useModal("preview-banner");

  // Selected banner for editing/viewing
  const [selectedBanner, setSelectedBanner] =
    useState<BannerResponseDto | null>(null);

  // Table state
  const {
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    handleFilterChange,
    getQueryParams,
    getSortDirection,
    isSorted,
    getFilterValue,
  } = useTable({
    key: "banners",
    defaultPageSize: 25,
  });

  // API hooks
  const {
    banners,
    stats,
    isLoading,
    handleCreate: createBanner,
    handleUpdate: updateBanner,
    handleDelete: deleteBanner,
    refetch,
  } = useBanners();

  // Table columns
  const columns: DataTableColumn<BannerResponseDto>[] = useMemo(
    () => [
      {
        key: "id",
        title: "ID",
        width: 80,
        sortable: true,
        render: (value: unknown) => `#${value}`,
      },
      {
        key: "image",
        title: "Preview",
        width: 120,
        render: (value: unknown) => (
          <div className="flex justify-center">
            <img
              src={value as string}
              alt="Banner preview"
              className="w-16 h-10 object-cover rounded border"
            />
          </div>
        ),
      },
      {
        key: "image",
        title: "Image URL",
        width: 200,
        render: (value: unknown) => (
          <div className="max-w-xs truncate" title={value as string}>
            {value as string}
          </div>
        ),
      },
      {
        key: "createdAt",
        title: "Created",
        sortable: true,
        render: (value: unknown) =>
          new Date(value as string).toLocaleDateString(),
      },
      {
        key: "updatedAt",
        title: "Updated",
        sortable: true,
        render: (value: unknown) =>
          new Date(value as string).toLocaleDateString(),
      },
    ],
    []
  );

  // Table actions for each row
  const actions: DataTableAction<BannerResponseDto>[] = useMemo(
    () => [
      {
        key: "view",
        label: "View",
        icon: <Eye className="w-4 h-4" />,
        variant: "ghost",
        onClick: (banner) => {
          setSelectedBanner(banner);
          previewModal.open();
        },
      },
      {
        key: "edit",
        label: "Edit",
        icon: <Edit className="w-4 h-4" />,
        variant: "ghost",
        onClick: (banner) => {
          setSelectedBanner(banner);
          editModal.open();
        },
      },
      {
        key: "delete",
        label: "Delete",
        icon: <Trash2 className="w-4 h-4" />,
        variant: "ghost",
        onClick: async (banner) => {
          await deleteBanner(banner.id);
        },
      },
    ],
    [deleteBanner, editModal, previewModal]
  );

  // Handle create success
  const handleCreateSuccess = () => {
    createModal.close();
    refetch();
    success("Success", "Banner created successfully");
  };

  // Handle edit success
  const handleEditSuccess = () => {
    editModal.close();
    setSelectedBanner(null);
    refetch();
    success("Success", "Banner updated successfully");
  };

  return (
    <div className="space-y-6">
      {/* Page Header with Stats */}
      <PageHeader
        title="Banners"
        action={{
          label: "Add Banner",
          onClick: createModal.open,
          icon: <Plus className="w-4 h-4" />,
          variant: "primary",
        }}
      >
        {/* Custom stats display */}
        <div className="flex gap-4 text-sm">
          <div className="text-gray-600 dark:text-gray-400">
            <span className="font-medium text-gray-900 dark:text-white">
              {stats?.total || 0}
            </span>{" "}
            Total
          </div>
        </div>
      </PageHeader>

      {/* Banner Limit Warning */}
      {banners.length >= 4 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center">
            <ImageIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" />
            <p className="text-yellow-800 dark:text-yellow-200">
              Maximum of 4 banners allowed. Delete existing banners to add new
              ones.
            </p>
          </div>
        </div>
      )}

      <div className="banners-table-container relative">
        <div className="overflow-visible">
          <DataTable<BannerResponseDto>
            columns={columns}
            data={banners}
            actions={actions}
            rowKey="id"
            loading={isLoading}
            scroll={{x: "100%"}}
          />
        </div>
      </div>

      {/* Inline styles for better table layout */}
      <style>{`
      // .banners-table-container * {
      //   overflow: visible !important;
      // }
      `}</style>
      {/* Modals */}
      <BannerFormModal
        isOpen={createModal.isOpen}
        onClose={createModal.close}
        onSuccess={handleCreateSuccess}
        mode="create"
      />

      <BannerFormModal
        isOpen={editModal.isOpen}
        onClose={() => {
          editModal.close();
          setSelectedBanner(null);
        }}
        onSuccess={handleEditSuccess}
        mode="edit"
        banner={selectedBanner}
      />

      <BannerPreviewModal
        isOpen={previewModal.isOpen}
        onClose={previewModal.close}
        banner={selectedBanner}
      />
    </div>
  );
};

export default BannersPage;
